import type { VerlaufEintrag } from '@nac2s/shared'
import { useEffect, useState } from 'react'
import { Hinweis } from '@/teile/Hinweis'
import { Status, type Ton } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { ladeVerlauf } from '../api/verlauf.js'
import { Seitenkopf } from '../layout/AppShell.js'

/**
 * Der eigene Verlauf — Wählaufträge mit dem Zustand aus den Push-Events.
 *
 * Solange die Anrufereignisse nicht eingerichtet sind, bleiben Anrufe bei
 * „abgeschickt": Der Dienst weiss dann nur, dass er den Auftrag gestellt hat.
 */
export function Verlauf() {
  const [zustand, setZustand] = useState<
    | { status: 'laedt' }
    | { status: 'da'; eintraege: VerlaufEintrag[] }
    | { status: 'fehler'; meldung: string }
  >({ status: 'laedt' })

  useEffect(() => {
    let aktiv = true
    ladeVerlauf(100)
      .then((v) => {
        if (aktiv) setZustand({ status: 'da', eintraege: v.eintraege })
      })
      .catch((error: unknown) => {
        if (aktiv) {
          setZustand({
            status: 'fehler',
            meldung: error instanceof ApiError ? error.message : 'Unbekannter Fehler',
          })
        }
      })
    return () => {
      aktiv = false
    }
  }, [])

  return (
    <div>
      <Seitenkopf
        beisatz="Ihre Anrufe"
        titel="Verlauf"
        satz="Die über die App ausgelösten Anrufe — mit Zustand, sobald sipgate ihn meldet."
      />
      <div className="flex flex-col gap-6 px-6 pb-8">
        {zustand.status === 'laedt' ? (
          <Laedt>Verlauf wird geladen …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : zustand.eintraege.length === 0 ? (
          <Leer titel="Noch keine Anrufe">
            Sobald Sie über den Hotkey wählen, steht der Anruf hier.
          </Leer>
        ) : (
          <Anrufliste eintraege={zustand.eintraege} />
        )}
        {zustand.status === 'da' && zustand.eintraege.some((e) => e.zustand === 'dialing') ? (
          <Hinweis art="info" titel={'Ein Anruf steht auf „abgeschickt"?'}>
            sipgate hat den Auftrag angenommen, aber noch nicht gemeldet, dass Ihr Telefon klingelt.
            Bleibt es dabei, hat das gewählte Telefon nicht abgehoben oder war nicht erreichbar —
            nach drei Minuten steht der Anruf dann auf „Telefon hat nicht abgehoben".
          </Hinweis>
        ) : null}
      </div>
    </div>
  )
}

export function Anrufliste({ eintraege }: { eintraege: VerlaufEintrag[] }) {
  return (
    <Tabelle kopf={['Wann', 'Nummer', 'Zustand', 'Dauer']}>
      {eintraege.map((e) => {
        const [ton, wort] = zustandswort(e)
        return (
          <tr key={e.id} className="border-b border-secondary last:border-b-0">
            <td className="py-3 pr-4 text-sm text-tertiary tabular-nums">
              {zeitpunkt(e.angefragt)}
            </td>
            <td className="py-3 pr-4 font-mono text-[13px] text-primary">{e.nummer}</td>
            <td className="py-3 pr-4">
              <Status ton={ton}>{wort}</Status>
            </td>
            <td className="py-3 text-sm text-tertiary tabular-nums">
              {e.dauerSekunden === null ? '—' : dauer(e.dauerSekunden)}
            </td>
          </tr>
        )
      })}
    </Tabelle>
  )
}

/** Zustand in Worten — und in der Farbe der Lage. */
function zustandswort(e: VerlaufEintrag): [Ton, string] {
  switch (e.zustand) {
    case 'requested':
    case 'dialing':
      return ['wartet', 'abgeschickt']
    case 'ringing':
      return ['wartet', 'klingelt']
    case 'answered':
      return ['gut', 'im Gespräch']
    case 'ended':
      return e.grund === 'normalClearing' || e.angenommen
        ? ['gut', 'beendet']
        : ['ruhig', grundwort(e.grund)]
    case 'failed':
      return [
        'stoerung',
        e.grund === 'deviceNoAnswer' ? 'Telefon hat nicht abgehoben' : 'nicht angenommen',
      ]
    case 'unknown_end':
      return ['ruhig', 'ohne Ende']
  }
}

function grundwort(grund: string | null): string {
  switch (grund) {
    case 'busy':
      return 'besetzt'
    case 'noAnswer':
      return 'nicht abgenommen'
    case 'cancel':
      return 'abgebrochen'
    case 'congestion':
      return 'kein Weg'
    case 'notFound':
      return 'Nummer unbekannt'
    case 'forwarded':
      return 'weitergeleitet'
    default:
      return 'beendet'
  }
}

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function dauer(sekunden: number): string {
  const m = Math.floor(sekunden / 60)
  const s = sekunden % 60
  return `${m}:${String(s).padStart(2, '0')} min`
}
